/**
 * CategoryTree — recursive marketplace category navigation widget
 * (STORY-003-007).
 *
 * Two render contexts share this widget: the browse filter sidebar
 * (STORY-003-005) and the search-results filter sidebar (STORY-003-008). It
 * renders a `role="tree"` of recursive `CategoryNode`s and owns:
 *
 *   - the expand/collapse state (auto-expanding the ancestors of the active
 *     node on mount / when the active id changes — AC#2);
 *   - URL synchronisation: clicking a node writes `?category=<uuid>` and the
 *     active node is read back from the same param on mount (AC#2/#3);
 *   - the WAI-ARIA Tree keyboard model with a roving tabIndex (AC#7): Arrow
 *     Up/Down move focus between *visible* treeitems, Right expands (or steps
 *     into the first child), Left collapses (or steps to the parent);
 *   - the loading skeleton (rendered immediately, so it appears well within the
 *     100ms budget — AC#9) and the error + Retry fallback (AC#10).
 *
 * `onSelect` is invoked once per click in addition to the URL write so callers
 * (browse page) can react (e.g. reset pagination). When `controlledActiveId`
 * is supplied the component uses it instead of the URL param — used by the
 * breadcrumb-driven surfaces and by tests.
 *
 * Tokens come from the `--g-*` Tailwind mirror; strings from the `marketplace`
 * i18n namespace. No hand-rolled colors, no hardcoded copy.
 */
import { RotateCw } from "lucide-react";
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { iconProps } from "@/lib/icons";

import { CategoryNode } from "@/features/marketplace/CategoryNode";
import {
  findPathInTree,
  useCategoryTree,
  type Category,
} from "@/features/marketplace/useCategoryTree";

export interface CategoryTreeProps {
  /**
   * Active category. When omitted the active id is read from the `?category=`
   * URL param (the browse-page contract). When provided it wins over the URL —
   * lets a parent drive selection (and keeps tests deterministic).
   */
  controlledActiveId?: string | null;
  /** Fires once per node click, alongside the `?category=` URL write (AC#3). */
  onSelect?: (categoryId: string) => void;
  /** Sidebar vs. full-page layout affordance (visual only at this scale). */
  mode?: "sidebar" | "fullpage";
  /** Accessible name for the tree landmark. */
  "aria-label"?: string;
}

const CATEGORY_PARAM = "category";

/** Flatten the tree into visible-order ids, honouring the expanded set. */
function visibleOrder(
  tree: readonly Category[],
  expandedIds: ReadonlySet<string>,
): string[] {
  const out: string[] = [];
  const walk = (nodes: readonly Category[]): void => {
    for (const node of nodes) {
      out.push(node.id);
      if (node.children.length > 0 && expandedIds.has(node.id)) {
        walk(node.children);
      }
    }
  };
  walk(tree);
  return out;
}

/** Build a parent-id lookup so ArrowLeft can step to a node's parent. */
function buildParentMap(tree: readonly Category[]): Map<string, string | null> {
  const map = new Map<string, string | null>();
  const walk = (nodes: readonly Category[], parentId: string | null): void => {
    for (const node of nodes) {
      map.set(node.id, parentId);
      walk(node.children, node.id);
    }
  };
  walk(tree, null);
  return map;
}

function CategoryTreeSkeleton(): ReactElement {
  const { t } = useTranslation("marketplace");
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={t("category.loading")}
      data-testid="category-tree-skeleton"
      className="flex flex-col gap-2 py-1"
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          aria-hidden="true"
          className="g-skel h-4 rounded-full"
          style={{
            marginLeft: `${(i % 3) * 0.75}rem`,
            width: `${88 - (i % 3) * 14}%`,
          }}
        />
      ))}
    </div>
  );
}

export function CategoryTree({
  controlledActiveId,
  onSelect,
  mode = "sidebar",
  "aria-label": ariaLabel,
}: CategoryTreeProps): ReactElement {
  const { t } = useTranslation("marketplace");
  const [searchParams, setSearchParams] = useSearchParams();
  const { tree, isLoading, error, refetch } = useCategoryTree();

  const urlActiveId = searchParams.get(CATEGORY_PARAM);
  const activeCategoryId =
    controlledActiveId !== undefined ? controlledActiveId : urlActiveId;

  // Auto-expand the ancestors of the active node (AC#2). Recomputed when the
  // tree resolves or the active id changes.
  const ancestorIds = useMemo<string[]>(() => {
    if (!activeCategoryId) return [];
    const path = findPathInTree(tree, activeCategoryId);
    // Every node on the path except the target itself should be expanded so
    // the target is visible; expanding the target too is harmless.
    return path.map((n) => n.id);
  }, [tree, activeCategoryId]);

  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  // Merge freshly-computed ancestors into the expanded set (never collapse what
  // the user already opened). Runs when the ancestor chain changes.
  useEffect(() => {
    if (ancestorIds.length === 0) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      for (const id of ancestorIds) next.add(id);
      return next;
    });
  }, [ancestorIds]);

  const parentMap = useMemo(() => buildParentMap(tree), [tree]);

  // Roving focus: which treeitem owns tabIndex=0. Defaults to the active node,
  // else the first root.
  const [focusedId, setFocusedId] = useState<string | null>(null);
  useEffect(() => {
    if (focusedId !== null) return;
    if (activeCategoryId) {
      setFocusedId(activeCategoryId);
    } else if (tree.length > 0) {
      setFocusedId(tree[0]?.id ?? null);
    }
  }, [tree, activeCategoryId, focusedId]);

  const handleSelect = (categoryId: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set(CATEGORY_PARAM, categoryId);
    setSearchParams(next);
    setFocusedId(categoryId);
    onSelect?.(categoryId);
  };

  const handleToggle = (categoryId: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  // Pointer/tab focus landing on a treeitem updates the roving index — but skip
  // the redundant set when the effect-driven keyboard move already pointed
  // `focusedId` here (avoids a no-op state churn after `.focus()`).
  const handleNodeFocus = (categoryId: string): void => {
    setFocusedId((prev) => (prev === categoryId ? prev : categoryId));
  };

  // Move the roving focus to `categoryId`: update the tabIndex owner and move
  // DOM focus to its treeitem. Both happen inside the keydown handler (which
  // test runners wrap in act), so focus tracks arrow keys deterministically.
  const focusNode = (categoryId: string | null): void => {
    if (categoryId === null) return;
    setFocusedId(categoryId);
    document
      .querySelector<HTMLElement>(`[data-testid="category-node-${categoryId}"]`)
      ?.focus();
  };

  const handleTreeKeyDown = (
    event: React.KeyboardEvent<HTMLElement>,
    categoryId: string,
  ): void => {
    const order = visibleOrder(tree, expandedIds);
    const idx = order.indexOf(categoryId);
    const node = parentMap.has(categoryId)
      ? // Resolve the node via the path helper to read its children/expand state.
        findPathInTree(tree, categoryId).at(-1) ?? null
      : null;
    const hasChildren = (node?.children.length ?? 0) > 0;
    const isExpanded = expandedIds.has(categoryId);

    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        focusNode(order[idx + 1] ?? null);
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        focusNode(order[idx - 1] ?? null);
        break;
      }
      case "ArrowRight": {
        event.preventDefault();
        if (hasChildren && !isExpanded) {
          handleToggle(categoryId);
        } else if (hasChildren && isExpanded) {
          focusNode(node?.children[0]?.id ?? null);
        }
        break;
      }
      case "ArrowLeft": {
        event.preventDefault();
        if (hasChildren && isExpanded) {
          handleToggle(categoryId);
        } else {
          focusNode(parentMap.get(categoryId) ?? null);
        }
        break;
      }
      case "Enter":
      case " ": {
        event.preventDefault();
        handleSelect(categoryId);
        break;
      }
      default:
        break;
    }
  };

  if (isLoading) {
    return <CategoryTreeSkeleton />;
  }

  if (error) {
    return (
      <div
        role="alert"
        data-testid="category-tree-error"
        className="flex flex-col items-start gap-2 rounded-[10px] border border-[color:var(--g-rule)] bg-[color:var(--g-sunken)] p-3 text-sm text-foreground"
      >
        <p className="text-[color:var(--g-ink-2)]">{t("category.loadError")}</p>
        <button
          type="button"
          onClick={() => {
            void refetch();
          }}
          data-testid="category-tree-retry"
          className="inline-flex min-h-[44px] items-center gap-1.5 text-xs font-semibold text-[color:var(--g-primary)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <RotateCw {...iconProps("xs")} />
          {t("category.retry")}
        </button>
      </div>
    );
  }

  return (
    <ul
      role="tree"
      aria-label={ariaLabel ?? t("category.treeLabel")}
      data-testid="category-tree"
      data-mode={mode}
      // `g-ruled` draws the armature between ROOT departments only — nested
      // children stay unruled so the rule reads as "top-level boundary", not
      // as noise at every depth.
      className="g-ruled m-0 flex list-none flex-col border-t border-[color:var(--g-rule)] p-0"
    >
      {tree.map((node) => (
        <CategoryNode
          key={node.id}
          node={node}
          level={1}
          activeCategoryId={activeCategoryId}
          focusedId={focusedId}
          expandedIds={expandedIds}
          onSelect={handleSelect}
          onToggle={handleToggle}
          onTreeKeyDown={handleTreeKeyDown}
          onNodeFocus={handleNodeFocus}
        />
      ))}
    </ul>
  );
}
